import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

import { VisionFile, VisionFileState, endpoints, sbcParcelApi } from '../api/sbcparcel.api';

const STATE_LABELS: Record<VisionFileState, { label: string; color: 'default' | 'success' | 'error' }> = {
  pending: { label: 'Pending', color: 'default' },
  processed: { label: 'Processed', color: 'success' },
  error: { label: 'Error', color: 'error' },
};

const formatSize = (bytes: number): string =>
  bytes >= 1048576 ? `${(bytes / 1048576).toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`;

// Shown as a rough age rather than a timestamp: for an archived file this is when Vision moved
// it, not when it was sent, and a precise-looking time would claim more than it knows.
const formatAge = (value: string): string => {
  const when = new Date(value);
  if (Number.isNaN(when.getTime())) return '';
  const days = Math.floor((Date.now() - when.getTime()) / 86400000);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  return `${days} days ago`;
};

// Port of the "Upload to Vision" button in the SBC.ParcelInvoiceUI WinForm. No customer or week
// is sent: Vision reads the customer from the uploaded file's own "Customer Name" column.
const VisionUploadPage: React.FC = () => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadedUrls, setUploadedUrls] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [history, setHistory] = useState<VisionFile[] | null>(null);
  const [historyError, setHistoryError] = useState<string | null>(null);
  // The mount load and the post-upload reload can be in flight together, and a slow first one
  // finishing last would put the pre-upload list back. Only the newest request may write state.
  const historyRequest = useRef(0);
  const navigate = useNavigate();

  const loadHistory = useCallback(async () => {
    const requestId = historyRequest.current + 1;
    historyRequest.current = requestId;
    try {
      const result: any = await sbcParcelApi.getVisionFiles(50);
      if (requestId !== historyRequest.current) return;
      // `handleError` resolves with the error response rather than throwing, so a failure has to
      // be caught by shape. Rendering it as an empty list would read as "nothing has ever been
      // uploaded" — and a missing VISION_STORAGE_CONNECTION_STRING is the likeliest failure of
      // the lot, so that wrong answer would be the common one.
      if (!Array.isArray(result?.files)) {
        throw new Error(
          result?.data?.message || result?.message || 'Could not load recent uploads.'
        );
      }
      setHistory(result.files);
      setHistoryError(null);
    } catch (err: any) {
      if (requestId !== historyRequest.current) return;
      // A failure here never blocks an upload; it just says so instead of showing an empty list.
      setHistory([]);
      setHistoryError(err?.message || 'Could not load recent uploads.');
    }
  }, []);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      setSelectedFiles(Array.from(event.target.files));
      setUploadedUrls(null);
      setError(null);
    }
    // Reset so re-picking the same file still fires a change event.
    event.target.value = '';
  };

  const handleCancel = () => {
    navigate('/');
  };

  const handleSubmit = () => {
    if (selectedFiles.length === 0) {
      setError('Please select at least one file.');
      return;
    }
    setError(null);
    setConfirmOpen(true);
  };

  const handleConfirm = async () => {
    setConfirmOpen(false);
    setUploading(true);
    setError(null);
    try {
      const formData = new FormData();
      selectedFiles.forEach((file) => {
        formData.append('files', file);
      });
      const response = await fetch(endpoints.uploadVision, {
        method: 'POST',
        body: formData,
      });
      const body = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(body?.message || 'Upload failed');
      }
      setUploadedUrls(Array.isArray(body?.urls) ? body.urls : []);
      setSelectedFiles([]);
      loadHistory();
    } catch (err: any) {
      setError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', px: 3, py: 5 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h5" gutterBottom>
          Upload to Vision
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Upload invoice files for Vision to process.
        </Typography>
        <Button variant="contained" onClick={handleButtonClick} sx={{ mb: 2 }} disabled={uploading}>
          Choose Files
        </Button>
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: 'none' }}
          multiple
          onChange={handleFileChange}
        />
        {selectedFiles.length > 0 && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2">Selected Files:</Typography>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {selectedFiles.map((file, idx) => (
                <li key={idx}>{file.name}</li>
              ))}
            </ul>
          </Box>
        )}
        <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={selectedFiles.length === 0 || uploading}
            startIcon={uploading ? <CircularProgress size={18} /> : null}
          >
            Upload
          </Button>
          <Button variant="outlined" color="secondary" onClick={handleCancel} disabled={uploading}>
            {uploadedUrls ? 'Done' : 'Cancel'}
          </Button>
        </Stack>
        {error && (
          <Typography color="error" sx={{ mt: 2 }}>
            {error}
          </Typography>
        )}
        {uploadedUrls && (
          <Box sx={{ mt: 2 }}>
            <Typography color="success.main" variant="body2" sx={{ fontWeight: 600 }}>
              Successfully uploaded {uploadedUrls.length} file(s) to Vision.
            </Typography>
            {/* The WinForm lists the blob URLs only for small batches; longer ones just confirm. */}
            {uploadedUrls.length > 0 && uploadedUrls.length <= 5 && (
              <ul style={{ margin: '8px 0 0', paddingLeft: 20, wordBreak: 'break-all' }}>
                {uploadedUrls.map((url) => (
                  <li key={url}>
                    <Typography variant="caption">{url}</Typography>
                  </li>
                ))}
              </ul>
            )}
          </Box>
        )}
      </Paper>

      {/* Asked for so someone can check what has already gone up today before sending a file
          twice. Every file in these containers came from this workflow, so it is the history
          rather than a view of one. */}
      <Paper elevation={3} sx={{ p: 3, mt: 3 }}>
        <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 1.5 }}>
          <Typography variant="h6">Recent uploads</Typography>
          <Typography variant="body2" color="textSecondary">
            Last 50
          </Typography>
        </Stack>

        {historyError ? (
          <Typography variant="body2" color="error">
            {historyError}
          </Typography>
        ) : history === null ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={22} />
          </Box>
        ) : history.length === 0 ? (
          <Typography variant="body2" color="textSecondary">
            Nothing has been uploaded yet.
          </Typography>
        ) : (
          <Box
            sx={{
              maxHeight: 620,
              overflowY: 'auto',
              // Keeps the scrollbar off the rows rather than overlapping the status chips.
              pr: 1,
            }}
          >
            {history.map((file, index) => (
              <Box key={file.state + file.fileName}>
                {index > 0 && <Divider />}
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1.5}
                  sx={{ py: 1.1 }}
                >
                  <Typography
                    variant="body2"
                    sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    title={file.fileName}
                  >
                    {file.fileName}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ whiteSpace: 'nowrap' }}>
                    {formatAge(file.seenAt)}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ width: 72, textAlign: 'right' }}
                  >
                    {formatSize(file.size)}
                  </Typography>
                  <Chip
                    size="small"
                    label={STATE_LABELS[file.state].label}
                    color={STATE_LABELS[file.state].color}
                    variant={file.state === 'pending' ? 'outlined' : 'filled'}
                    sx={{ width: 92 }}
                  />
                </Stack>
              </Box>
            ))}
          </Box>
        )}
      </Paper>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirm Upload</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            You are about to upload {selectedFiles.length} file(s) to Vision:
            <ul style={{ margin: '8px 0', paddingLeft: 20 }}>
              {selectedFiles.map((file, idx) => (
                <li key={idx}>{file.name}</li>
              ))}
            </ul>
            Do you want to continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} color="secondary">
            No
          </Button>
          <Button onClick={handleConfirm} variant="contained" autoFocus>
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VisionUploadPage;
